import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Accordion, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import ISalesRep, { ISalesRepCreateOrUpdate, ISalesRepInitResult, ISalesRepFilters } from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import { formatCurrency, formatDate } from "utilities/StringUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { generateLog, getBaseDomain } from "utilities/MiscUtilities";
import moment from "moment";
import CountryChooser from "components/CountryChooser";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import MarkdownEditor from "components/MarkdownEditor";
import StateChooser from "components/StateChooser";
import IHealthRiskAssessment from "models/sql/IHealthRiskAssessment";
import DPSEntitySelector from "components/DPSEntitySelector";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import MiscService from "services/MiscService";

interface ISalesRepsDetailProps {
    baseData?: ISalesRepInitResult;
}

const SalesRepsDetail = (props: ISalesRepsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [dealer, setDealer] = useState<IDealer>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [parentSalesRep, setParentSalesRep] = useState<ISalesRep>();
    const { id }: any = useParams();
    const [salesRep, setSalesRep] = useState<ISalesRep | null>({
        company: "",
        email: "",
        fullName: "",
        phone: "",
        homeState: "",
        homeCountry: "",
        notes: "",
        systemDataJson: "",
        salesRepId: -1,
        code: "",
        airCardInviteCount: 0,
        hraInviteCount: 0,
        created: undefined,
        defaultRoles: "",
        dealerId: undefined,
        lastAccessRequest: undefined,
        accessRequests: 0,
        parentSalesRepId: undefined,
        managedCountries: "",
        managedStates: "",
        managedRegionName: "",
        canRequestDemo: undefined,
        isFireFighter: undefined,
        fireDepartmentId: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await SalesRepService.GetAsync(accessToken, id);

                    if (result.record) {
                        setSalesRep(result.record);

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        if (result.record.parentSalesRepId !== null && result.record.parentSalesRepId !== undefined) {
                            const parentRepResult = await SalesRepService.GetAsync(accessToken, result.record.parentSalesRepId);

                            if (parentRepResult.record) {
                                setParentSalesRep(parentRepResult.record);
                            }
                        }

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }
                    }
                    else {
                        setSalesRep(null);
                        globalContext.showErrorMessage("Unable to load Sales Rep");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const getDashboardAccessType = (entry: ISalesRep) => {
        if (entry && entry.salesRepId !== 0 && entry.dealer) {
            if (entry.dealer.primaryEmail === entry.email) {
                return "Dealer Dashboard";
            }

            return "Rep Dashboard";
        }

        return "";
    };

    const getRepDashboardAccessTokenAsync = async () => {
        if (salesRep && salesRep.email) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            const result = await MiscService.getRepDashboardAccessTokenAsync(accessToken, salesRep.email);

            if (result) {
                globalContext.showModal(
                    {
                        title: "Rep Dashboard Access Url",
                        content: <>
                            <p>
                                Copy the following URL to access the rep dashboard.<br />
                                <i style={{ color: "red", fontSize: "0.8rem" }}>Note: URL must be visited on a browser that is not already logged into the Admin Website (private mode is an option for testing.)</i>
                            </p>
                            <textarea
                                rows={4}
                                style={{ width: "100%" }}
                                value={`https://${getBaseDomain()}/dps/?dashboardToken=${result}`}
                                readOnly={true}
                            />
                        </>
                    }
                )
            }
            else {
                globalContext.showErrorMessage("Unable to get Dashboard Access Token");
            }

            globalContext.setLoading(false);

        }
    };

    const deleteAsync = async () => {
        if (salesRep && salesRep.salesRepId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await SalesRepService.DeleteAsync(accessToken, salesRep.salesRepId);
            globalContext.setLoading(false);
            navigate("/Reps");
        }
    };

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    const submitFormAsync = async () => {
        if (salesRep) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: ISalesRepCreateOrUpdate = {
                company: salesRep.company,
                email: salesRep.email,
                fullName: salesRep.fullName,
                phone: salesRep.phone,
                homeState: salesRep.homeState,
                homeCountry: salesRep.homeCountry,
                notes: salesRep.notes,
                systemDataJson: salesRep.systemDataJson,
                salesRepId: salesRep.salesRepId,
                code: salesRep.code,
                hraInviteCount: salesRep.hraInviteCount,
                airCardInviteCount: salesRep.airCardInviteCount,
                created: salesRep.created,
                defaultRoles: salesRep.defaultRoles,
                dealerId: salesRep.dealerId,
                lastAccessRequest: salesRep.lastAccessRequest,
                accessRequests: salesRep.accessRequests,
                parentSalesRepId: salesRep.parentSalesRepId,
                managedCountries: salesRep.managedCountries,
                managedStates: salesRep.managedStates,
                managedRegionName: salesRep.managedRegionName,
                canRequestDemo: salesRep.canRequestDemo,
                isFireFighter: salesRep.isFireFighter,
                fireDepartmentId: salesRep.fireDepartmentId
            };

            await SalesRepService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.salesRepId === -1) {
                navigate(-1);
            }
        }
    };

    let debounceId = 0;

    const [parentSalesRepsFilter, setParentSalesRepsfilter] = useState<ISalesRepFilters>();
    const [parentSalesReps, setParentSalesReps] = useState<ISalesRep[]>([]);
    const [parentSalesRepNames, setParentSalesRepNames] = useState<string[]>();
    const [showParentSalesRepChooser, setShowParentSalesRepChooser] = useState(false);

    useEffect(
        () => {
            (async () => {
                if (parentSalesRepsFilter) {
                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await SalesRepService.GetAllAsync(accessToken, 0, 20, parentSalesRepsFilter);

                    if (result.records !== null) {
                        setParentSalesReps(result.records);
                    }
                    else {
                        globalContextRef.current.showErrorMessage("Unable to load Reps Manager");
                    }
                }
            })();
        },
        [parentSalesRepsFilter]);

    const showSelectableParentSalesRep = (salesRep: ISalesRep) => (
        <Row className="align-items-end">
            <Col className="dynamiceObjectSelectorButton">
                {
                    !showParentSalesRepChooser &&
                    <Form.Group
                        className="p-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            setParentSalesRepsfilter({ dealerIds: salesRep.dealerId ? [salesRep.dealerId] : undefined });
                            setShowParentSalesRepChooser(!showParentSalesRepChooser);
                        }} >
                        <Form.Label>
                            Reps Manager
                            {
                                salesRep?.parentSalesRepId &&
                                <>
                                    &nbsp;
                                    <FontAwesomeIcon
                                        icon={faUpRightFromSquare as IconProp}
                                        style={{ cursor: "pointer" }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            window.open(`/DPS/Reps/Detail/${salesRep.parentSalesRepId}`)
                                        }} />
                                </>
                            }
                        </Form.Label>
                        <h2>{parentSalesRep?.fullName || "None Selected"}</h2>
                    </Form.Group>
                }

                <div>
                    {
                        showParentSalesRepChooser &&
                        <>
                            <h3>Choose a Sales Rep</h3>
                            <Row className="align-items-end">
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Rep Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            autoComplete="none"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setParentSalesRepNames(e.target.value.split(","));
                                                    window.clearTimeout(debounceId);
                                                    setHasFormDataChanged(true);
                                                    debounceId = window.setTimeout(() => {
                                                        setParentSalesRepsfilter({ ...parentSalesRepsFilter, names: parentSalesRepNames });
                                                    }, 500);
                                                }
                                            }
                                            value={parentSalesRepNames?.join(",") || ""}
                                        /><br />
                                        <strong>Click a Sales Rep to select.</strong>
                                        <Table striped bordered hover size="sm" className="m-1">
                                            <tbody>
                                                {
                                                    !parentSalesReps || parentSalesReps.length < 1 &&
                                                    <tr><td>No matches.</td></tr>
                                                }
                                                {
                                                    parentSalesReps.filter(r => r.salesRepId !== salesRep.salesRepId).map(
                                                        entry => <tr
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                setSalesRep({ ...salesRep, parentSalesRepId: entry.salesRepId });
                                                                setParentSalesRep(entry);
                                                                setShowParentSalesRepChooser(false);
                                                                setHasFormDataChanged(true);
                                                            }}><td>{entry.fullName}</td></tr>
                                                    )
                                                }

                                            </tbody>
                                        </Table><br />
                                        <Button
                                            className="mb-1"
                                            style={{ width: "100%" }}
                                            onClick={
                                                () => setShowParentSalesRepChooser(false)}>Cancel Parent Rep Update</Button>

                                        <Button
                                            style={{ width: "100%" }}
                                            onClick={
                                                () => {
                                                    setSalesRep({ ...salesRep, parentSalesRepId: undefined });
                                                    setParentSalesRep(undefined);
                                                    setShowParentSalesRepChooser(false);
                                                    setHasFormDataChanged(true);
                                                }}>Set to None</Button>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    }
                </div>
            </Col>
        </Row>
    );

    if (salesRep) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <>
                                            <strong>{salesRep.fullName} ({salesRep.fullName})</strong><br />
                                            <a href={`https://${getBaseDomain()}/Public/hra?r=${salesRep.code}`} target="_blank">HRA Invite Link</a>
                                            {
                                                (salesRep.canRequestDemo || (salesRep.dealer?.canRequestDemo && salesRep.canRequestDemo !== false)) &&
                                                <>
                                                    <br />
                                                    <a
                                                        target="_blank"
                                                        href={`/Public/DemoRequest?r=${salesRep.code}`}>
                                                        Demo Request Form
                                                    </a></>
                                            }
                                        </>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New SalesRep</strong>
                                    }
                                    {
                                        <>
                                            <br />
                                            <button
                                                className="btn btn-primary mt-2"
                                                onClick={getRepDashboardAccessTokenAsync}
                                            >Get {getDashboardAccessType(salesRep)} Access Url</button>
                                        </>
                                    }
                                </Card.Title>

                                <DPSEntitySelector<IDealer, IDealerFilters>
                                    entityFilterSearchPropName={"dealerNames"}
                                    entityService={DealerService}
                                    entityTypeName="Dealer"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={dealer}
                                    generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Dealers/Detail/${entry.dealerId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setSalesRep({ ...salesRep, dealerId: newEntry?.dealerId || undefined });
                                        setDealer(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                {showSelectableParentSalesRep(salesRep)}

                                <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                    entityFilterSearchPropName={"fireDeptNames"}
                                    entityService={FireDepartmentService}
                                    entityTypeName="Department"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireDepartment}
                                    generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip} ${entry.hqCountry}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Departments/Detail/${entry.fireDepartmentId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setSalesRep({ ...salesRep, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                        setFireDepartment(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Full Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, fullName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.fullName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Company</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, company: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.company}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Created</Form.Label>
                                            {
                                                <Form.Control
                                                    type="date"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, created: e.target.value ? e.target.value : undefined });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.created ? moment(salesRep.created).format('yyyy-MM-DD') : undefined}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, email: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.email}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Phone</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, phone: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.phone}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setSalesRep({ ...salesRep, homeCountry: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={salesRep.homeCountry}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>State</Form.Label>
                                            {
                                                <StateChooser
                                                    onChange={newVal => {
                                                        setSalesRep({ ...salesRep, homeState: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    defaultValue={salesRep.homeState}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Access Requests</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={true}
                                                    value={salesRep.accessRequests ?? "0"}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Last Access Request</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={true}
                                                    value={formatDate(salesRep.lastAccessRequest)}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, code: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.code}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>AirCard Registrations</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, airCardInviteCount: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.airCardInviteCount || 0}
                                                />
                                            }
                                        </Form.Group></Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HRA Invites</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setSalesRep({ ...salesRep, hraInviteCount: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={salesRep.hraInviteCount || 0}
                                                />
                                            }
                                        </Form.Group></Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Can Request Demos (Dealer: {salesRep.dealer?.canRequestDemo === true ? "true" : "false"})</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setSalesRep({ ...salesRep, canRequestDemo: e.target.value === "true" ? true : e.target.value === "false" ? false : undefined });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={salesRep.canRequestDemo === true ? "true" : salesRep.canRequestDemo === false ? "false" : ""}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>FireFighter?</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setSalesRep({ ...salesRep, isFireFighter: e.target.value === "true" ? true : e.target.value === "false" ? false : undefined });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={salesRep.isFireFighter === true ? "true" : salesRep.isFireFighter === false ? "false" : ""}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Default Roles</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                setSalesRep({ ...salesRep, defaultRoles: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={salesRep.defaultRoles}
                                                    />
                                                }
                                            </Form.Group></Col>
                                    </Row>
                                }

                                {
                                    salesRep.inverseParentSalesRep && salesRep.inverseParentSalesRep.length > 0 &&
                                    <Row>
                                        <Col>
                                            <h2>Child Reps</h2>
                                            <Table className="lookup-table">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Email</th>
                                                        <th>Phone</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        salesRep.inverseParentSalesRep?.map((e, index) =>
                                                            <tr
                                                                key={index}
                                                                style={{ cursor: "pointer" }}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    window.open(`/DPS/Reps/Detail/${e.salesRepId}`)
                                                                }}><td>
                                                                    <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.fullName}
                                                                </td>
                                                                <td>
                                                                    {e.email}
                                                                </td>
                                                                <td>
                                                                    {e.phone}
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Equipment") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0"
                                                    style={{
                                                    }}>
                                                    <Accordion.Header><strong>Demos</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table className="lookup-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Product</th>
                                                                    <th>Serial</th>
                                                                    <th>Demo Requested</th>
                                                                    <th>Demo Approved</th>
                                                                    <th>Demo Started</th>
                                                                    <th>Shipped</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    salesRep.equipment?.filter(e => e.demoRequestDate && e.demoRequestDate && e.demoApprovalDate).map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            className={(e.demoRequestDate && !e.demoStartDate) ? "table-action-required" : ""}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/DPS/Equipment/Detail/${e.equipmentId}`)
                                                                            }}><td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.product?.name}
                                                                            </td>
                                                                            <td>
                                                                                {e.serialNumber}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.demoRequestDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.demoApprovalDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.demoStartDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.shippedDate)}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Equipment") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0"
                                                    style={{
                                                    }}>
                                                    <Accordion.Header><strong>Equipment</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table className="lookup-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Product</th>
                                                                    <th>Serial</th>
                                                                    <th>Demo Requested</th>
                                                                    <th>Demo Started</th>
                                                                    <th>Demo Ended</th>
                                                                    <th>Registered</th>
                                                                    <th>Shipped</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    salesRep.equipment?.filter(e => e.registrationDate).map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            className={(e.demoRequestDate && !e.demoStartDate) ? "table-action-required" : ""}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/DPS/Equipment/Detail/${e.equipmentId}`)
                                                                            }}><td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.product?.name}
                                                                            </td>
                                                                            <td>
                                                                                {e.serialNumber}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.demoRequestDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.demoStartDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.demoEndDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.registrationDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDate(e.shippedDate)}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Opportunities") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0"
                                                    style={{
                                                    }}>
                                                    <Accordion.Header><strong>Opportunities</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table className="lookup-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Created</th>
                                                                    <th>Department</th>
                                                                    <th>Total Value</th>
                                                                    <th>Stage</th>
                                                                    <th>Source</th>
                                                                    <th>Score</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    salesRep.opportunities?.map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/DPS/Opportunities/Detail/${e.opportunityId}`)
                                                                            }}><td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.created ? formatDate(e.created) : ""}
                                                                            </td>
                                                                            <td>
                                                                                {e.fireDepartment?.fireDeptName || ""}
                                                                            </td>
                                                                            <td>
                                                                                {formatCurrency(e?.totalValue || 0)}
                                                                            </td>
                                                                            <td>
                                                                                {e.stage}
                                                                            </td>
                                                                            <td>
                                                                                {e.airCardId && "AirCard"}{e.airCardId && e.healthRiskAssessmentId && " "}{e.healthRiskAssessmentId && "HRA"}
                                                                            </td>
                                                                            <td>
                                                                                {
                                                                                    e.airCard && e.airCard.score ||
                                                                                    e.healthRiskAssessment && calcScore(e.healthRiskAssessment) ||
                                                                                    "-"
                                                                                }
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row className="align-items-end">
                                        <Col>
                                            <Card className="mb-2">
                                                <Card.Body>
                                                    <Card.Title>
                                                        <strong>Log</strong><br />
                                                    </Card.Title>
                                                    {generateLog<ISalesRep>(salesRep)}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                }
                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setSalesRep({ ...salesRep, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={salesRep.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }

                                <Row>
                                    <Col>
                                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                            <Accordion.Item
                                                eventKey="0"
                                                style={{
                                                    "backgroundColor": "#ffc4c4"
                                                }}>
                                                <Accordion.Header><strong>TSM Configuration (TSM's Only!)</strong></Accordion.Header>
                                                <Accordion.Body>
                                                    <Row>
                                                        <Col>
                                                            <Row className="align-items-end">
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Managed Region Name</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="text"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setSalesRep({ ...salesRep, managedRegionName: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={salesRep.managedRegionName}
                                                                            />
                                                                        }
                                                                    </Form.Group></Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Managed States</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="text"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setSalesRep({ ...salesRep, managedStates: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={salesRep.managedStates}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col>
                                                                    <Form.Group className="mb-3" >
                                                                        <Form.Label>Managed Countries</Form.Label>
                                                                        {
                                                                            <Form.Control
                                                                                type="text"
                                                                                onChange={
                                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                        setSalesRep({ ...salesRep, managedCountries: e.target.value });
                                                                                        setHasFormDataChanged(true);
                                                                                    }
                                                                                }
                                                                                value={salesRep.managedCountries}
                                                                            />
                                                                        }
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the SalesRep, please try again.</h1>
            </>
        );
    }
}

export default SalesRepsDetail;
